import React, { useState, useEffect } from "react";
import { FaBolt, FaInfoCircle, FaBitcoin } from "react-icons/fa";
import useMiningCalculator from "../hooks/useMiningCalculator";

export default function SimpleMiningCalculator() {
  const [currency, setCurrency] = useState("RUB");
  const [coin, setCoin] = useState("BTC");
  const [algorithm, setAlgorithm] = useState("SHA-256");

  // числовые состояния
  const [hashrate, setHashrate] = useState(100);
  const [power, setPower] = useState(3000);
  const [costRub, setCostRub] = useState(5.25);
  const [costUsd, setCostUsd] = useState(0.12);
  const [farmCost, setFarmCost] = useState(15000);

  // строковые состояния для input
  const [hashrateInput, setHashrateInput] = useState(String(hashrate));
  const [powerInput, setPowerInput] = useState(String(power));
  const [costRubInput, setCostRubInput] = useState(String(costRub));
  const [costUsdInput, setCostUsdInput] = useState(String(costUsd));
  const [farmCostInput, setFarmCostInput] = useState(String(farmCost));
  const [hashrateUnit, setHashrateUnit] = useState("TH/s");

  const [showTooltip, setShowTooltip] = useState(false);
  const [activeTab, setActiveTab] = useState("Day");
  const [showFarmTooltip, setShowFarmTooltip] = useState(false);

  // 🔥 Реальные данные с бэкенда
  const [coinData, setCoinData] = useState(null);
  const [loading, setLoading] = useState(false);

  const isNumericString = (v) =>
    v !== "" && v !== "-" && v !== "." && !Number.isNaN(Number(v));

  const algorithmList = [
    "BTC|SHA-256",
    "LTC|Scrypt",
    "DOGE|Scrypt",
    "ETC|Etchash",
    "ZEC|Equihash",
    "KAS|kHeavyHash",
    "CKB|Eaglesong",
    "ALEO|zkSNARK",
    "ALPH|Blake3",
    "DASH|x11",
    "HNS|Handshake",
    "KDA|Blake2S",
  ];

  // 🎯 Загрузка данных с бэкенда при смене монеты
  useEffect(() => {
    const fetchCoinData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/coins/${coin}`);
        const data = await response.json();
        setCoinData(data);
        console.log(`📦 Данные для ${coin}:`, data);
      } catch (error) {
        console.error(`❌ Ошибка загрузки ${coin}:`, error);
        setCoinData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCoinData();
  }, [coin]);

  // 🎯 Обновление единиц хэшрейта при смене алгоритма
  useEffect(() => {
    const units = {
      "BTC|SHA-256": "TH/s",
      "LTC|Scrypt": "GH/s",
      "DOGE|Scrypt": "GH/s",
      "ETC|Etchash": "GH/s",
      "ZEC|Equihash": "KH/s",
      "KAS|kHeavyHash": "TH/s",
      "CKB|Eaglesong": "TH/s",
      "ALEO|zkSNARK": "MH/s",
      "ALPH|Blake3": "TH/s",
      "DASH|x11": "GH/s",
      "HNS|Handshake": "TH/s",
      "KDA|Blake2S": "TH/s",
    };
    setHashrateUnit(units[`${coin}|${algorithm}`] || "H/s");
  }, [coin, algorithm]);

  const {
    results,
    difficulty: hookDifficulty,
    blockReward: hookBlockReward,
    btcUsd,
    farmCostUSD,
    usdRub,
    powerW,
    electricityUsdPerKWh,
    electricityRubPerKWh,
    poolFeePercent: hookPoolFee,
    setPoolFeePercent,
    setPowerW,
    setElectricityUsdPerKWh,
    setElectricityRubPerKWh,
    setFarmCostUSD,
  } = useMiningCalculator({
    currency,
    coin,
    algorithm,
    hashrate,
    hashrateUnit,
    initialPowerW: power,
    initialElectricityUsdPerKWh: costUsd,
    initialElectricityRubPerKWh: costRub,
    initialFarmCostUSD: farmCost,
  });

  // 🔥 Используем реальные цены из API
  const coinPriceUsd = coinData?.price || btcUsd || 0;
  
  // 🔥 Используем реальную сложность и награду из API
  const difficulty = coinData?.difficulty || hookDifficulty;
  const blockReward = coinData?.blockReward || hookBlockReward;

  // 🎯 Корректировочные коэффициенты - теперь все = 1, так как данные реальные
  const algorithmCorrection = {
    "LTC|Scrypt": 1,
    "DOGE|Scrypt": 1,
    "ETC|Etchash": 1,
    "ZEC|Equihash": 1,
    "KAS|kHeavyHash": 1,
    "CKB|Eaglesong": 1,
    "ALEO|zkSNARK": 1,
    "ALPH|Blake3": 1,
    "DASH|x11": 1,
    "HNS|Handshake": 1,
    "KDA|Blake2S": 1,
  };

  const correctionKey = `${coin}|${algorithm}`;
  const correctionFactor = algorithmCorrection[correctionKey] ?? 1;

  // 📉 Применяем корректировку (теперь она ничего не меняет)
  const correctedResults = results && {
    ...results,
    coinPerDay: results.coinPerDay * correctionFactor,
    coinPerWeek: results.coinPerWeek * correctionFactor,
    coinPerMonth: results.coinPerMonth * correctionFactor,
    coinPerYear: results.coinPerYear * correctionFactor,
    revenueDay: results.revenueDay * correctionFactor,
    revenueWeek: results.revenueWeek * correctionFactor,
    revenueMonth: results.revenueMonth * correctionFactor,
    revenueYear: results.revenueYear * correctionFactor,
    profitDay: results.profitDay * correctionFactor,
    profitWeek: results.profitWeek * correctionFactor,
    profitMonth: results.profitMonth * correctionFactor,
    profitYear: results.profitYear * correctionFactor,
    paybackDays: results.paybackDays / correctionFactor,
    paybackWeeks: results.paybackWeeks / correctionFactor,
    paybackMonths: results.paybackMonths / correctionFactor,
  };

  const formatCurrency = (value) => {
    if (!isFinite(value) || value === undefined || value === null) return "—";
    const formatter = new Intl.NumberFormat("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const formatted = formatter.format(Math.abs(value));
    return currency === "RUB" ? `${formatted} RUB` : `$${formatted}`;
  };

  const formatNumber = (value, decimals = 8) => {
    if (!isFinite(value) || value === undefined || value === null) return "—";
    return value.toFixed(decimals);
  };

  const forceRecalc = () => setHashrate((v) => v);

  // 🎯 Обработчик смены монеты
  const handleCoinChange = (e) => {
    const [c, a] = e.target.value.split("|");
    setCoin(c);
    setAlgorithm(a);
  };

  // 🎯 Сброс всех полей
  const handleReset = () => {
    setCoin("BTC");
    setAlgorithm("SHA-256");
    setHashrate(100);
    setPower(3000);
    setCostRub(5.25);
    setCostUsd(0.12);
    setFarmCost(15000);
    setPowerW(3000);
    setElectricityUsdPerKWh(0.12);
    setElectricityRubPerKWh(5.25);
    setFarmCostUSD(currency === "USD" ? 15000 : 15000 / (usdRub || 90));
    setHashrateInput("100");
    setPowerInput("3000");
    setCostRubInput("5.25");
    setCostUsdInput("0.12");
    setFarmCostInput("15000");
  };

  return (
    <div className="p-2 max-w-3xl mx-auto font-sans text-gray-100 min-h-screen pt-2">
      {/* Индикатор загрузки */}
      {loading && (
        <div className="fixed top-4 right-4 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-semibold">
          Загрузка данных...
        </div>
      )}

      <div className="flex items-center gap-3 mb-8 mt-2 font-comfortaa">
        <img src="/logo.png" alt="Logo" className="h-16 w-auto ml-[10px]" />
        <div className="leading-tight text-[#FCDE5A] font-bold text-lg">
          <div>МАЙНИНГ-КАЛЬКУЛЯТОР</div>
          <div>ОТ CAPMINER.RU</div>
        </div>
      </div>

      {/* Валюта */}
      <div className="flex justify-end mb-2">
        <div className="inline-flex rounded-lg overflow-hidden border border-gray-700">
          <button
            onClick={() => setCurrency("RUB")}
            className={`px-4 py-2 font-semibold ${
              currency === "RUB" 
                ? "bg-yellow-400 text-gray-900" 
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
            }`}
          >
            RUB
          </button>
          <button
            onClick={() => setCurrency("USD")}
            className={`px-4 py-2 font-semibold ${
              currency === "USD" 
                ? "bg-yellow-400 text-gray-900" 
                : "bg-gray-800 hover:bg-gray-700 text-gray-300"
            }`}
          >
            USD
          </button>
        </div>
      </div>

      {/* Входные данные */}
      <div className="grid grid-cols-2 gap-4 bg-gray-800 p-4 rounded-lg shadow-lg">
        {/* Алгоритм */}
        <div>
          <label className="block text-sm text-gray-400">Алгоритм</label>
          <select
            value={`${coin}|${algorithm}`}
            onChange={handleCoinChange}
            className="w-full mt-1 p-2 rounded bg-gray-700 border border-gray-600"
          >
            {algorithmList.map((key) => {
              const [c, a] = key.split("|");
              return (
                <option key={key} value={key}>
                  {c} | {a}
                </option>
              );
            })}
          </select>
        </div>

        {/* Хэшрейт */}
        <div>
          <label className="block text-sm text-gray-400">
            Хэшрейт ({hashrateUnit})
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={hashrateInput}
            onChange={(e) => {
              const v = e.target.value;
              setHashrateInput(v);
              if (isNumericString(v) || v === "") {
                const num = v === "" ? 0 : Number(v);
                setHashrate(num);
              }
            }}
            onFocus={(e) => {
              if (e.target.value === "0") e.target.setSelectionRange(0, 0);
            }}
            className="w-full mt-1 p-2 rounded bg-gray-700 border border-gray-600"
            step="0.01"
          />
        </div>

        {/* Потребление */}
        <div>
          <label className="block text-sm text-gray-400">Потребление</label>
          <div className="relative mt-1">
            <input
              type="text"
              inputMode="numeric"
              value={powerInput}
              onChange={(e) => {
                const v = e.target.value;
                setPowerInput(v);
                if (isNumericString(v) || v === "") {
                  const num = v === "" ? 0 : Number(v);
                  setPower(num);
                  setPowerW(num);
                }
              }}
              onFocus={(e) => { 
                if (e.target.value === "0") e.target.setSelectionRange(0, 0); 
              }}
              className="w-full p-2 pr-16 rounded bg-gray-700 border border-gray-600"
            />
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 pointer-events-none">
              Вт
            </span>
          </div>
        </div>

        {/* Цена энергии */}
        <div>
          <label className="block text-sm text-gray-400">Цена энергии</label>
          <div className="relative mt-1">
            <input
              type="text"
              inputMode="decimal"
              value={currency === "RUB" ? costRubInput : costUsdInput}
              onChange={(e) => {
                const v = e.target.value;
                if (currency === "RUB") {
                  setCostRubInput(v);
                  if (isNumericString(v) || v === "") {
                    const num = v === "" ? 0 : Number(v);
                    setCostRub(num);
                    setElectricityRubPerKWh(num);
                  }
                } else {
                  setCostUsdInput(v);
                  if (isNumericString(v) || v === "") {
                    const num = v === "" ? 0 : Number(v);
                    setCostUsd(num);
                    setElectricityUsdPerKWh(num);
                  }
                }
              }}
              onFocus={(e) => { 
                if (e.target.value === "0") e.target.setSelectionRange(0, 0); 
              }}
              className="w-full p-2 pr-20 rounded bg-gray-700 border border-gray-600"
            />
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 pointer-events-none">
              {currency === "RUB" ? "₽/кВт·ч" : "$/кВт·ч"}
            </span>
          </div>
        </div>

        {/* Стоимость фермы */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <span className="text-sm text-gray-400">Цена фермы</span>
            <FaInfoCircle
              className="text-gray-400 cursor-pointer hover:text-yellow-400"
              onClick={() => setShowFarmTooltip(true)}
            />
          </div>
          <div className="relative mt-1">
            <input
              type="text"
              inputMode="numeric"
              value={farmCostInput}
              onChange={(e) => {
                const v = e.target.value;
                setFarmCostInput(v);
                if (isNumericString(v) || v === "") {
                  const num = v === "" ? 0 : Number(v);
                  setFarmCost(num);
                  setFarmCostUSD(currency === "USD" ? num : num / (usdRub || 90));
                }
              }}
              onFocus={(e) => { 
                if (e.target.value === "0") e.target.setSelectionRange(0, 0); 
              }}
              className="w-full p-2 pr-8 rounded bg-gray-700 border border-gray-600"
            />
            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 pointer-events-none">
              {currency === "RUB" ? "₽" : "$"}
            </span>
          </div>

          {/* Модалка с подсказкой */}
          {showFarmTooltip && (
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setShowFarmTooltip(false)}
              />
              <div className="relative w-80 p-4 bg-gray-700 text-gray-100 text-sm rounded-lg shadow-xl z-50">
                <div className="font-semibold mb-2">Стоимость фермы</div>
                <p>
                  Совокупная стоимость оборудования для расчета окупаемости.
                </p>
                <button
                  onClick={() => setShowFarmTooltip(false)}
                  className="mt-3 px-3 py-1 bg-yellow-400 text-gray-900 rounded hover:bg-yellow-300"
                >
                  Закрыть
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Комиссия пула */}
        <div>
          <label className="block text-sm text-gray-400">Комиссия пула</label>
          <div className="relative mt-1">
            <select
              value={hookPoolFee ?? 2}
              onChange={(e) => setPoolFeePercent(Number(e.target.value))}
              className="w-full p-2 rounded bg-gray-700 border border-gray-600"
            >
              {Array.from({ length: 21 }, (_, i) => i * 0.5).map((val) => (
                <option key={val} value={val}>
                  {val} %
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Кнопки */}
      <div className="flex gap-4 mt-4">
        <button
          onClick={handleReset}
          className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
        >
          Очистить
        </button>

        <button
          onClick={forceRecalc}
          className="flex-1 px-4 py-2 bg-yellow-400 text-gray-900 font-semibold hover:bg-yellow-300 rounded"
        >
          Посчитать
        </button>
      </div>

      {/* Результаты */}
      {correctedResults && !loading && (
        <div className="w-full py-2 text-sm text-gray-300 mt-4">
          {/* Табы */}
          <div className="flex justify-center gap-3 mb-4">
            {["Day", "Week", "Month"].map((tab) => {
              const label = tab === "Day" ? "1 день" : tab === "Week" ? "1 неделя" : "1 месяц";
              return (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.("light");
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    activeTab === tab 
                      ? "bg-yellow-500 text-black shadow" 
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Контент выбранного таба */}
          <div className="bg-gray-700/50 rounded-xl p-4 space-y-6">
            {/* Доход */}
            <div>
              <div className="text-gray-400 text-sm mb-3">
                Ваш приблизительный доход с <span className="text-yellow-400">capminer.ru</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  {coin === "BTC" && <FaBitcoin className="text-yellow-400" />}
                  <span className="font-mono text-green-400 text-[15px]">
                    {formatNumber(
                      activeTab === "Day"
                        ? correctedResults.coinPerDay
                        : activeTab === "Week"
                        ? correctedResults.coinPerWeek
                        : correctedResults.coinPerMonth
                    )}{" "}
                    {coin}
                  </span>
                  <span className="text-gray-400 text-sm">
                    / {activeTab === "Day" ? "день" : activeTab === "Week" ? "неделя" : "месяц"}
                  </span>
                </div>
                <div className="text-gray-200 text-sm">
                  ≈ {formatCurrency(
                    activeTab === "Day"
                      ? correctedResults.revenueDay
                      : activeTab === "Week"
                      ? correctedResults.revenueWeek
                      : correctedResults.revenueMonth
                  )}{" "}
                  / {activeTab === "Day" ? "день" : activeTab === "Week" ? "неделя" : "месяц"}
                </div>
              </div>
            </div>

            {/* Цена электроэнергии */}
            <div>
              <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                Цена электроэнергии
                <span className="relative">
                  <FaInfoCircle
                    className="text-gray-400 cursor-pointer hover:text-yellow-400"
                    onClick={() => setShowTooltip(!showTooltip)}
                  />
                  {showTooltip && (
                    <div className="fixed inset-0 flex items-center justify-center z-50">
                      <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowTooltip(false)}
                      />
                      <div className="relative w-80 p-4 bg-gray-700 text-gray-100 text-sm rounded-lg shadow-xl z-50">
                        <div className="font-semibold mb-2">Как считается расход</div>
                        <p className="mb-2">
                          - Электроэнергия: Вт → кВт × цена кВт·ч × 24ч × дни
                          <br />
                          Комиссия пула не входит в расход, она уменьшает доход.
                        </p>
                        <button
                          onClick={() => setShowTooltip(false)}
                          className="mt-2 px-3 py-1 bg-yellow-400 text-gray-900 rounded hover:bg-yellow-300"
                        >
                          Закрыть
                        </button>
                      </div>
                    </div>
                  )}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                {/* В монете */}
                <div className="flex items-center gap-2">
                  {coin === "BTC" && <FaBitcoin className="text-yellow-400" />}
                  {(() => {
                    const daysMultiplier =
                      activeTab === "Day" ? 1 : activeTab === "Week" ? 7 : 30;
                    const kWh = (powerW / 1000) * 24 * daysMultiplier;
                    const electricityCostRub = kWh * (typeof costRub === "number" ? costRub : 0);
                    const electricityInCoin =
                      coinPriceUsd && usdRub
                        ? electricityCostRub / (coinPriceUsd * usdRub)
                        : 0;
                    return (
                      <span className="font-mono text-red-400 text-[15px]">
                        {formatNumber(electricityInCoin)} {coin}
                      </span>
                    );
                  })()}
                  <span className="text-gray-400 text-sm">
                    / {activeTab === "Day" ? "день" : activeTab === "Week" ? "неделя" : "месяц"}
                  </span>
                </div>
                {/* В валюте */}
                <div className="text-gray-200 text-sm">
                  ≈ {formatCurrency(
                    (() => {
                      const daysMultiplier =
                        activeTab === "Day" ? 1 : activeTab === "Week" ? 7 : 30;
                      const kWh = (powerW / 1000) * 24 * daysMultiplier;
                      const electricityCostRub = kWh * (typeof costRub === "number" ? costRub : 0);
                      return currency === "USD"
                        ? (usdRub ? electricityCostRub / usdRub : 0)
                        : electricityCostRub;
                    })()
                  )}{" "}
                  / {activeTab === "Day" ? "день" : activeTab === "Week" ? "неделя" : "месяц"}
                </div>
              </div>
            </div>

            {/* Итоговая прибыль */}
            <div>
              <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                Итоговая прибыль
              </div>
              <div className="flex flex-col gap-1">
                {/* В монете */}
                <div className="flex items-center gap-2">
                  {coin === "BTC" && <FaBitcoin className="text-yellow-400" />}
                  {(() => {
                    const daysMultiplier =
                      activeTab === "Day" ? 1 : activeTab === "Week" ? 7 : 30;
                    const kWh = (powerW / 1000) * 24 * daysMultiplier;
                    const electricityCostRub = kWh * (typeof costRub === "number" ? costRub : 0);
                    const electricityInCoin =
                      coinPriceUsd && usdRub
                        ? electricityCostRub / (coinPriceUsd * usdRub)
                        : 0;
                    const incomeInCoin =
                      activeTab === "Day"
                        ? correctedResults.coinPerDay
                        : activeTab === "Week"
                        ? correctedResults.coinPerWeek
                        : correctedResults.coinPerMonth;
                    const profitInCoin = incomeInCoin - electricityInCoin;
                    return (
                      <span className="font-mono text-yellow-400 text-[15px]">
                        {formatNumber(profitInCoin)} {coin}
                      </span>
                    );
                  })()}
                  <span className="text-gray-400 text-sm">
                    / {activeTab === "Day" ? "день" : activeTab === "Week" ? "неделя" : "месяц"}
                  </span>
                </div>
                {/* В валюте */}
                <div className="text-gray-200 text-sm">
                  ≈ {formatCurrency(
                    (() => {
                      const daysMultiplier =
                        activeTab === "Day" ? 1 : activeTab === "Week" ? 7 : 30;
                      const kWh = (powerW / 1000) * 24 * daysMultiplier;
                      const electricityCostRub = kWh * (typeof costRub === "number" ? costRub : 0);
                      const incomeFiat =
                        activeTab === "Day"
                          ? correctedResults.revenueDay
                          : activeTab === "Week"
                          ? correctedResults.revenueWeek
                          : correctedResults.revenueMonth;
                      const electricityFiat =
                        currency === "USD"
                          ? (usdRub ? electricityCostRub / usdRub : 0)
                          : electricityCostRub;
                      return incomeFiat - electricityFiat;
                    })()
                  )}{" "}
                  / {activeTab === "Day" ? "день" : activeTab === "Week" ? "неделя" : "месяц"}
                </div>
              </div>
            </div>

            {/* Окупаемость */}
            <div className="mt-6 p-4 bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FaBolt className="text-yellow-400 text-base" />
                <span className="text-gray-200 font-semibold text-sm">Окупаемость</span>
              </div>
              {(() => {
                const daysMultiplier =
                  activeTab === "Day" ? 1 : activeTab === "Week" ? 7 : 30;
                const kWh = (powerW / 1000) * 24 * daysMultiplier;
                const electricityCostRub = kWh * (typeof costRub === "number" ? costRub : 0);
                const incomeFiat =
                  activeTab === "Day"
                    ? correctedResults.revenueDay
                    : activeTab === "Week"
                    ? correctedResults.revenueWeek
                    : correctedResults.revenueMonth;
                const electricityFiat =
                  currency === "USD"
                    ? (usdRub ? electricityCostRub / usdRub : 0)
                    : electricityCostRub;
                const profitRaw = incomeFiat - electricityFiat;
                const profitPerDay =
                  activeTab === "Day"
                    ? profitRaw
                    : activeTab === "Week"
                    ? profitRaw / 7
                    : profitRaw / 30;
                const farmCostCurrency =
                  currency === "USD"
                    ? farmCostUSD || 0
                    : farmCost || 0;
                const paybackDays =
                  profitPerDay > 0 ? farmCostCurrency / profitPerDay : null;

                return paybackDays ? (
                  <div className="text-sm text-gray-300">
                    <div className="mb-1">
                      <span className="text-gray-400">Срок окупаемости: </span>
                      <span className="font-mono text-yellow-400">
                        {paybackDays.toFixed(1)} дней
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Формула: <span className="text-gray-400">стоимость фермы ÷ прибыль в день</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">
                    Окупаемость:{" "}
                    <span className="text-yellow-400">не окупается с этими данными!</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Курс валют и информация о монете */}
      {coinData && (
        <div className="text-gray-500 text-xs mb-3">
          <div>Курс USD: 1 USD = {(usdRub ? usdRub.toFixed(2) : "—")} ₽</div>
          <div>Курс {coin}: 1 {coin} = {formatNumber(coinPriceUsd, 2)} USD</div>
          <div className="font-mono text-gray-500">
            <br />
            <strong>Текущая сложность:</strong>{" "}
            {difficulty != null ? difficulty.toLocaleString() : "—"}
            <br />
            <strong>Награда за блок:</strong>{" "}
            {blockReward != null ? formatNumber(blockReward, 4) : "—"}
            <br />
            <strong>Алгоритм:</strong> {coinData.algorithm || algorithm}
            <br />
            <strong>Хешрейт сети:</strong> {coinData.hashrate || "—"}
            {coinData.source && (
              <>
                <br />
                <strong>Источник:</strong> {coinData.source}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}